import type { ZipTie } from '@erp/workflows';
import {
  DOUBLE_CLICK_MS,
  snapAngle,
  type Point,
} from 'src/lib/workflows/zipTieGeometry';

export type PointerModifiers = {
  shift: boolean;
  alt: boolean;
  ctrl: boolean;
  meta: boolean;
};

export type ZipDrag = {
  pointerId: number;
  tieId: string;
  last: Point;
  moved: boolean;
  rotate: boolean;
  angleOffset: number;
  ties: ZipTie[];
};

export type ZipPointerBegin =
  | { kind: 'ignore' }
  | { kind: 'cut'; point: Point }
  | { kind: 'remove'; tieId: string }
  | { kind: 'drag'; drag: ZipDrag; now: number };

function pointerAngle(origin: Point, point: Point): number {
  return Math.atan2(point.y - origin.y, point.x - origin.x);
}

export function tieWithColor(
  tie: ZipTie,
  tieId: string,
  color: string | undefined,
): ZipTie {
  if (tie.id !== tieId) return tie;
  if (!color) {
    const { color: _removed, ...rest } = tie;
    return rest;
  }
  return { ...tie, color };
}

function movedTie(
  ties: readonly ZipTie[],
  tieId: string,
  patch: Partial<ZipTie>,
): ZipTie[] {
  return ties.map((tie) => (tie.id === tieId ? { ...tie, ...patch } : tie));
}

function isBareZ(event: {
  code: string;
  altKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
}): boolean {
  return (
    event.code === 'KeyZ' && !event.altKey && !event.ctrlKey && !event.metaKey
  );
}

/**
 * Whether Z is held after `event`. Any Z release ends it, whatever modifiers
 * are down by then; only a bare Z press outside a text field starts it.
 */
export function zipKeyHeld(
  held: boolean,
  event: Parameters<typeof isBareZ>[0] & { type: string },
  typing: boolean,
): boolean {
  if (event.code !== 'KeyZ') return held;
  if (event.type === 'keyup') return false;
  return held || (!typing && isBareZ(event));
}

export function wantsZipStroke(
  keyHeld: boolean,
  modifiers: PointerModifiers,
): boolean {
  return (
    keyHeld &&
    !modifiers.shift &&
    !modifiers.alt &&
    !modifiers.ctrl &&
    !modifiers.meta
  );
}

/** What a primary press on the canvas should start. Pure so the listeners stay thin. */
export function beginZipPointer(input: {
  readOnly: boolean;
  keyHeld: boolean;
  modifiers: PointerModifiers;
  tie: ZipTie | null;
  point: Point;
  ties: readonly ZipTie[];
  pointerId: number;
  lastDown: { id: string | null; time: number };
  now: number;
}): ZipPointerBegin {
  if (wantsZipStroke(input.keyHeld, input.modifiers) && !input.readOnly) {
    return { kind: 'cut', point: input.point };
  }
  if (!input.tie || input.readOnly) return { kind: 'ignore' };
  if (
    input.lastDown.id === input.tie.id &&
    input.now - input.lastDown.time < DOUBLE_CLICK_MS
  ) {
    return { kind: 'remove', tieId: input.tie.id };
  }
  return {
    kind: 'drag',
    now: input.now,
    drag: {
      pointerId: input.pointerId,
      tieId: input.tie.id,
      last: input.point,
      moved: false,
      rotate: input.modifiers.shift,
      angleOffset:
        input.tie.angle - pointerAngle(input.tie.position, input.point),
      ties: [...input.ties],
    },
  };
}

/** The strap after one pointer move. Returns the same drag when nothing changed. */
export function updateZipDrag(
  drag: ZipDrag,
  point: Point,
  snap: boolean,
): ZipDrag {
  const tie = drag.ties.find((item) => item.id === drag.tieId);
  if (!tie) return drag;
  if (drag.rotate) {
    return {
      ...drag,
      moved: true,
      ties: movedTie(drag.ties, tie.id, {
        angle: snapAngle(
          pointerAngle(tie.position, point) + drag.angleOffset,
          snap,
        ),
      }),
    };
  }
  const dx = point.x - drag.last.x;
  const dy = point.y - drag.last.y;
  if (dx === 0 && dy === 0) return drag;
  return {
    ...drag,
    last: point,
    moved: true,
    ties: movedTie(drag.ties, tie.id, {
      position: { x: tie.position.x + dx, y: tie.position.y + dy },
    }),
  };
}
